import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authenticatedFetch } from '@/lib/auth';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import DpaSigningPage from '@/components/DpaSigningPage';

/**
 * Onboarding-wrapper som henter DPA-status for innlogget bruker og
 * bestemmer hvilken DPA som må signeres neste.
 *
 * URL: /onboarding/dpa (auto-velger) eller /onboarding/dpa?type=X&party_type=Y&party_id=Z
 */
export default function DpaOnboardingPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextDpa, setNextDpa] = useState<any>(null);
  const [allSigned, setAllSigned] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await authenticatedFetch('/api/dpa/status');
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);

        const explicitType = params.get('type');
        const explicitPartyType = params.get('party_type');
        const explicitPartyId = params.get('party_id');

        if (explicitType && explicitPartyType && explicitPartyId) {
          setNextDpa({
            dpa_type: explicitType,
            party_type: explicitPartyType,
            party_id: explicitPartyId,
            lms_type: params.get('lms_type') || undefined,
            lms_integration_id: params.get('lms_integration_id') || undefined
          });
          setLoading(false);
          return;
        }

        // Finn første DPA som mangler signering / trenger re-signering
        const needsSigning = (data.dpa_status || []).find((d: any) =>
          !d.signed || !d.is_current_version_signed
        );
        if (needsSigning) {
          setNextDpa({
            dpa_type: needsSigning.dpa_type,
            party_type: needsSigning.party_type,
            party_id: needsSigning.party_id,
            lms_type: needsSigning.lms_type,
            lms_integration_id: needsSigning.lms_integration_id
          });
        } else {
          setAllSigned(true);
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [params]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-kf-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-900">Kunne ikke hente DPA-status</h3>
            <p className="text-sm text-red-800 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (allSigned) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-900 mt-3">Alle databehandleravtaler er signert</h2>
          <p className="text-gray-600 mt-2">Din organisasjon har signert alle nødvendige avtaler.</p>
          <button onClick={() => navigate('/onboarding')} className="mt-4 px-6 py-2 bg-kf-primary text-white rounded hover:bg-kf-primary-dark">
            Fortsett onboarding
          </button>
        </div>
      </div>
    );
  }

  if (!nextDpa) return null;

  return (
    <DpaSigningPage
      dpaType={nextDpa.dpa_type}
      partyType={nextDpa.party_type}
      partyId={nextDpa.party_id}
      lmsType={nextDpa.lms_type}
      lmsIntegrationId={nextDpa.lms_integration_id}
    />
  );
}
